import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, AreaChart, Area, ComposedChart
} from 'recharts';
import { 
  DollarSign, TrendingUp, TrendingDown, Calendar, Filter, RefreshCw,
  PieChart as PieChartIcon, BarChart3, LineChart as LineChartIcon,
  Target, AlertCircle, CheckCircle, Clock
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../api';

const FinanceDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({});
  const [statistiques, setStatistiques] = useState({});
  const [anneesAcademiques, setAnneesAcademiques] = useState([]);
  
  // Filtres
  const [filters, setFilters] = useState({
    annee_academique: '',
    periode: 'mois',
    date_debut: '',
    date_fin: ''
  });

  const [selectedChart, setSelectedChart] = useState('tendances');

  useEffect(() => {
    fetchData();
    fetchAnnees();
  }, []);

  useEffect(() => {
    if (filters.annee_academique || filters.date_debut || filters.date_fin) {
      fetchStatistiques();
    }
  }, [filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Essayer d'abord l'endpoint simplifié
      const response = await api.get('/finance/dashboard-simple/');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Erreur dashboard simplifié, tentative endpoint principal:', error);
      try {
        const response = await api.get('/finance/dashboard/');
        setDashboardData(response.data);
      } catch (secondError) {
        console.error('Erreur dashboard principal:', secondError);
        toast.error('Erreur lors du chargement du dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnees = async () => {
    try {
      const response = await api.get('/etablissement/annees-academiques/');
      setAnneesAcademiques(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des années:', error);
    }
  };

  const fetchStatistiques = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.annee_academique) params.append('annee_academique', filters.annee_academique);
      if (filters.periode) params.append('periode', filters.periode);
      if (filters.date_debut) params.append('date_debut', filters.date_debut);
      if (filters.date_fin) params.append('date_fin', filters.date_fin);

      const response = await api.get(`/finance/statistiques/?${params.toString()}`);
      setStatistiques(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
      toast.error('Erreur lors du chargement des statistiques avancées');
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      annee_academique: '',
      periode: 'mois',
      date_debut: '',
      date_fin: ''
    });
    setStatistiques({});
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Préparer les données pour les graphiques
  const pieData = statistiques.stats_categories?.map((cat, index) => ({
    name: cat.categorie,
    value: cat.total,
    color: COLORS[index % COLORS.length]
  })) || [];

  const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#F97316', '#84CC16'];

  const stats = dashboardData?.statistiques || statistiques?.resume || {};

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section des filtres */}
      <div className="bg-white p-6 rounded-lg shadow border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filtres et Analyses
          </h3>
          <button
            onClick={resetFilters}
            className="flex items-center px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Réinitialiser
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Année Académique
            </label>
            <select
              value={filters.annee_academique}
              onChange={(e) => handleFilterChange('annee_academique', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent"
            >
              <option value="">Toutes les années</option>
              {anneesAcademiques.map(annee => (
                <option key={annee.id} value={annee.id}>
                  {annee.libelle} {annee.status ? '(Active)' : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Période
            </label>
            <select
              value={filters.periode}
              onChange={(e) => handleFilterChange('periode', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent"
            >
              <option value="mois">Par mois</option>
              <option value="semaine">Par semaine</option>
              <option value="jour">Par jour</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date début
            </label>
            <input
              type="date"
              value={filters.date_debut}
              onChange={(e) => handleFilterChange('date_debut', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date fin
            </label>
            <input
              type="date"
              value={filters.date_fin}
              onChange={(e) => handleFilterChange('date_fin', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-lg shadow border border-green-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-500 mr-4">
              <TrendingUp className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-green-800">Total Entrées</h3>
              <p className="text-2xl font-bold text-green-900">{formatCurrency(stats.total_entrees)}</p>
              <p className="text-xs text-green-600 mt-1">
                {stats.nombre_transactions} transactions
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-lg shadow border border-red-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-500 mr-4">
              <TrendingDown className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-red-800">Total Sorties</h3>
              <p className="text-2xl font-bold text-red-900">{formatCurrency(stats.total_sorties)}</p>
              <p className="text-xs text-red-600 mt-1">
                {stats.nombre_depenses} dépenses
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-lg shadow border border-blue-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-500 mr-4">
              <DollarSign className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-blue-800">Solde Net</h3>
              <p className={`text-2xl font-bold ${(stats.total_entrees - stats.total_sorties) >= 0 ? 'text-blue-900' : 'text-red-900'}`}>
                {formatCurrency(stats.total_entrees - stats.total_sorties)}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Bénéfice/Perte
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6 rounded-lg shadow border border-yellow-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-500 mr-4">
              <Clock className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-yellow-800">En Attente</h3>
              <p className="text-2xl font-bold text-yellow-900">{dashboardData?.statistiques?.depenses_en_attente || 0}</p>
              <p className="text-xs text-yellow-600 mt-1">
                Dépenses à approuver
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Résumé des dernières activités */}
      {(dashboardData.derniers_mouvements?.length > 0 || dashboardData.dernieres_depenses?.length > 0) && (
        <div className="space-y-6">
          {/* Si les deux sections existent, les afficher côte à côte */}
          {dashboardData.derniers_mouvements?.length > 0 && dashboardData.dernieres_depenses?.length > 0 ? (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Derniers mouvements */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Header avec gradient */}
                <div className="bg-gradient-to-r from-[#202654] to-[#202654]/90 px-6 py-4">
                  <div className="flex items-center text-white">
                    <div className="bg-white/10 p-2 rounded-lg mr-3">
                      <TrendingUp className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">Derniers Mouvements</h3>
                      <p className="text-blue-100 text-sm">Activités récentes de trésorerie</p>
                    </div>
                  </div>
                </div>

                {/* Contenu */}
                <div className="p-6">
              <div className="space-y-3">
                {dashboardData.derniers_mouvements.slice(0, 5).map((mouvement, index) => (
                      <div key={index} className="group hover:shadow-md transition-all duration-200 bg-gradient-to-r from-gray-50 to-white border border-gray-100 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                    <div className="flex items-center">
                            <div className={`p-2.5 rounded-xl mr-4 ${
                              mouvement.nature === 'ENTREE' 
                                ? 'bg-gradient-to-r from-green-500 to-green-600 shadow-green-200 shadow-lg' 
                                : 'bg-gradient-to-r from-red-500 to-red-600 shadow-red-200 shadow-lg'
                      }`}>
                        {mouvement.nature === 'ENTREE' ? 
                                <TrendingUp className="h-4 w-4 text-white" /> :
                                <TrendingDown className="h-4 w-4 text-white" />
                        }
                      </div>
                      <div>
                              <p className="text-sm font-semibold text-gray-900 group-hover:text-[#202654] transition-colors">
                          {mouvement.type_mouvement_display || mouvement.type_mouvement}
                        </p>
                              <div className="flex items-center mt-1">
                                <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                        <p className="text-xs text-gray-500">
                          {formatDate(mouvement.date_mouvement)}
                        </p>
                              </div>
                      </div>
                    </div>
                    <div className="text-right">
                            <p className={`text-lg font-bold ${
                        mouvement.nature === 'ENTREE' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {mouvement.nature === 'ENTREE' ? '+' : '-'}{formatCurrency(mouvement.montant)}
                      </p>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              mouvement.nature === 'ENTREE' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {mouvement.nature === 'ENTREE' ? 'Entrée' : 'Sortie'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Footer avec statistiques */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">
                        {dashboardData.derniers_mouvements.length} mouvement{dashboardData.derniers_mouvements.length > 1 ? 's' : ''}
                      </span>
                      <button className="text-[#202654] hover:text-[#1a1f4a] font-medium hover:underline transition-colors">
                        Voir tous →
                      </button>
                    </div>
                  </div>
                </div>
              </div>

          {/* Dernières dépenses */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Header avec gradient */}
                <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4">
                  <div className="flex items-center text-white">
                    <div className="bg-white/10 p-2 rounded-lg mr-3">
                      <AlertCircle className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">Dernières Dépenses</h3>
                      <p className="text-purple-100 text-sm">Demandes et approbations</p>
                    </div>
                  </div>
                </div>

                {/* Contenu */}
                <div className="p-6">
              <div className="space-y-3">
                {dashboardData.dernieres_depenses.slice(0, 5).map((depense, index) => (
                      <div key={index} className="group hover:shadow-md transition-all duration-200 bg-gradient-to-r from-gray-50 to-white border border-gray-100 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                    <div className="flex items-center">
                            <div className={`p-2.5 rounded-xl mr-4 ${
                              depense.statut === 'APPROUVE' 
                                ? 'bg-gradient-to-r from-green-500 to-green-600 shadow-green-200 shadow-lg' : 
                              depense.statut === 'REJETE' 
                                ? 'bg-gradient-to-r from-red-500 to-red-600 shadow-red-200 shadow-lg' 
                                : 'bg-gradient-to-r from-yellow-500 to-yellow-600 shadow-yellow-200 shadow-lg'
                      }`}>
                        {depense.statut === 'APPROUVE' ? 
                                <CheckCircle className="h-4 w-4 text-white" /> :
                          depense.statut === 'REJETE' ?
                                <AlertCircle className="h-4 w-4 text-white" /> :
                                <Clock className="h-4 w-4 text-white" />
                        }
                      </div>
                      <div>
                              <p className="text-sm font-semibold text-gray-900 group-hover:text-[#202654] transition-colors">
                          {depense.categorie_libelle}
                        </p>
                              <div className="flex items-center mt-1">
                                <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                        <p className="text-xs text-gray-500">
                          {formatDate(depense.date_demande)}
                        </p>
                              </div>
                      </div>
                    </div>
                    <div className="text-right">
                            <p className="text-lg font-bold text-gray-900">
                        {formatCurrency(depense.montant)}
                      </p>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              depense.statut === 'APPROUVE' ? 'bg-green-100 text-green-800' : 
                              depense.statut === 'REJETE' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {depense.statut_display}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Footer avec statistiques */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">
                        {dashboardData.dernieres_depenses.length} dépense{dashboardData.dernieres_depenses.length > 1 ? 's' : ''}
                      </span>
                      <button className="text-[#202654] hover:text-[#1a1f4a] font-medium hover:underline transition-colors">
                        Voir toutes →
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Si une seule section existe, l'afficher en pleine largeur */
            <div className="w-full">
              {/* Derniers mouvements - pleine largeur */}
              {dashboardData.derniers_mouvements?.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  {/* Header avec gradient */}
                  <div className="bg-gradient-to-r from-[#202654] to-[#202654]/90 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-white">
                        <div className="bg-white/10 p-2 rounded-lg mr-3">
                          <TrendingUp className="h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold">Derniers Mouvements</h3>
                          <p className="text-blue-100 text-sm">Activités récentes de trésorerie</p>
                        </div>
                      </div>
                      <div className="text-white text-sm">
                        {dashboardData.derniers_mouvements.length} mouvement{dashboardData.derniers_mouvements.length > 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>

                  {/* Contenu en grille pour pleine largeur */}
                  <div className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                      {dashboardData.derniers_mouvements.slice(0, 6).map((mouvement, index) => (
                        <div key={index} className="group hover:shadow-md transition-all duration-200 bg-gradient-to-r from-gray-50 to-white border border-gray-100 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className={`p-2 rounded-xl mr-3 ${
                                mouvement.nature === 'ENTREE' 
                                  ? 'bg-gradient-to-r from-green-500 to-green-600 shadow-green-200 shadow-lg' 
                                  : 'bg-gradient-to-r from-red-500 to-red-600 shadow-red-200 shadow-lg'
                              }`}>
                                {mouvement.nature === 'ENTREE' ? 
                                  <TrendingUp className="h-4 w-4 text-white" /> :
                                  <TrendingDown className="h-4 w-4 text-white" />
                                }
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-gray-900 group-hover:text-[#202654] transition-colors truncate">
                                  {mouvement.type_mouvement_display || mouvement.type_mouvement}
                                </p>
                                <div className="flex items-center mt-1">
                                  <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                                  <p className="text-xs text-gray-500">
                                    {formatDate(mouvement.date_mouvement)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="mt-3 flex items-center justify-between">
                            <p className={`text-lg font-bold ${
                              mouvement.nature === 'ENTREE' ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {mouvement.nature === 'ENTREE' ? '+' : '-'}{formatCurrency(mouvement.montant)}
                            </p>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              mouvement.nature === 'ENTREE' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {mouvement.nature === 'ENTREE' ? 'Entrée' : 'Sortie'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Footer avec bouton voir plus */}
                   
                  </div>
                </div>
              )}

              {/* Dernières dépenses - pleine largeur */}
              {dashboardData.dernieres_depenses?.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  {/* Header avec gradient */}
                  <div className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-white">
                        <div className="bg-white/10 p-2 rounded-lg mr-3">
                          <AlertCircle className="h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold">Dernières Dépenses</h3>
                          <p className="text-purple-100 text-sm">Demandes et approbations</p>
                        </div>
                      </div>
                      <div className="text-white text-sm">
                        {dashboardData.dernieres_depenses.length} dépense{dashboardData.dernieres_depenses.length > 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>

                  {/* Contenu en grille pour pleine largeur */}
                  <div className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                      {dashboardData.dernieres_depenses.slice(0, 6).map((depense, index) => (
                        <div key={index} className="group hover:shadow-md transition-all duration-200 bg-gradient-to-r from-gray-50 to-white border border-gray-100 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className={`p-2 rounded-xl mr-3 ${
                                depense.statut === 'APPROUVE' 
                                  ? 'bg-gradient-to-r from-green-500 to-green-600 shadow-green-200 shadow-lg' : 
                                depense.statut === 'REJETE' 
                                  ? 'bg-gradient-to-r from-red-500 to-red-600 shadow-red-200 shadow-lg' 
                                  : 'bg-gradient-to-r from-yellow-500 to-yellow-600 shadow-yellow-200 shadow-lg'
                              }`}>
                                {depense.statut === 'APPROUVE' ? 
                                  <CheckCircle className="h-4 w-4 text-white" /> :
                                  depense.statut === 'REJETE' ?
                                  <AlertCircle className="h-4 w-4 text-white" /> :
                                  <Clock className="h-4 w-4 text-white" />
                                }
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-gray-900 group-hover:text-[#202654] transition-colors truncate">
                                  {depense.categorie_libelle}
                                </p>
                                <div className="flex items-center mt-1">
                                  <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                                  <p className="text-xs text-gray-500">
                                    {formatDate(depense.date_demande)}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="mt-3 flex items-center justify-between">
                            <p className="text-lg font-bold text-gray-900">
                              {formatCurrency(depense.montant)}
                            </p>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              depense.statut === 'APPROUVE' ? 'bg-green-100 text-green-800' : 
                              depense.statut === 'REJETE' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {depense.statut_display}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Footer avec bouton voir plus */}
                    <div className="mt-6 pt-4 border-t border-gray-100 text-center">
                      <button className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                        Voir toutes les dépenses →
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FinanceDashboard; 